import NiceModal from "@ebay/nice-modal-react";
import { endOfMonth, startOfMonth } from "date-fns";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { utils, writeFileXLSX } from "xlsx";

import Box from "components/Box";
import Button from "components/Button";
import DateRangeField from "components/DateRangeField";
import Empty from "components/Empty";
import IncomePreviewModal from "components/modals/IncomePreviewModal";
import RawCheckbox from "components/RawCheckbox";
import EnhancedSearchWithTypeField from "components/SearchWithTypeField";
import Select from "components/Select";
import SortButton from "components/SortButton";
import Table from "components/Table";
import TitleHeading from "components/TitleHeading";
import { useGetGrades } from "hooks/apis/grade";
import { useGetUsers } from "hooks/apis/user";
import useHandleParams from "hooks/useHandleParams";
import useSelection from "hooks/useSelection";
import format from "lib/format";
import { userSearchTypeOptions } from "utils/const";
import { addDashes } from "utils/utils";

type FormValues = {
  page: number;
  startDate?: string;
  endDate?: string;
  grade?: string;
  searchType?: string;
  searchText?: string;
  sort?: string;
};

const size = 1000;

const SettingsPaymentPage = () => {
  const { methods, searchRef } = useHandleParams<FormValues>({
    defaultValues: {
      startDate: format(startOfMonth(new Date()), "yyyy-MM-dd"),
      endDate: format(endOfMonth(new Date()), "yyyy-MM-dd"),
      grade: "all",
      searchType: "name",
      searchText: "",
      page: 1,
      sort: "salaryDesc",
    },
  });

  const form = methods.watch();

  const { data, totalItems, currentPage, isFetching } = useGetUsers({
    size,
    ...form,
  });
  const { data: grades } = useGetGrades();

  const { onSelectAll, onSelectItem, selection } = useSelection<number>(data);

  const gradeOptions = useMemo(
    () => [
      { label: "전체 등급", value: "all" },
      ...(grades || [])
        .filter((grade) => ![1, 2].includes(grade.id))
        .map((grade) => ({
          label: grade.name,
          value: `${grade.id}`,
        })),
    ],
    [grades],
  );

  const onExcelExport = () => {
    if (!selection.length || !data) {
      return;
    }
    const { startDate, endDate } = methods.getValues();
    const ws = utils.json_to_sheet(
      data
        .filter((user) => selection.includes(user.id))
        .map((user, index) => {
          const {
            grade,
            name,
            userId,
            phone,
            residentId1,
            residentId2,
            bank,
            bankNumber,
          } = user;

          return {
            번호: totalItems - index - size * (currentPage - 1),
            등급: grade.name,
            "이름(아이디)": `${name}(${userId})`,
            휴대전화번호: addDashes(phone),
            "답변 수": user.answerCount,
            "경고 수": user.reportCount,
            "총 급여":
              (user.answerCount! - user.reportCount!) * user.grade.amount,
            주민등록번호: `${residentId1}-${residentId2}`,
            "은행/계좌번호": `${bank}/${bankNumber}`,
          };
        }),
    );

    const autoFilterRange = "E1:G1";
    ws["!autofilter"] = { ref: autoFilterRange };
    const wb = utils.book_new();
    utils.book_append_sheet(
      wb,
      ws,
      `${format(startDate, "yyyy-MM-dd")}_${format(endDate, "yyyy-MM-dd")}`,
    );
    writeFileXLSX(
      wb,
      `급여_${format(startDate, "yyyy-MM-dd")}_${format(endDate, "yyyy-MM-dd")}.xlsx`,
    );
  };

  const onIncomePreview = (id: number) => {
    NiceModal.show(IncomePreviewModal, {
      id,
      startDate: form.startDate,
      endDate: form.endDate,
    });
  };

  return (
    <FormProvider {...methods}>
      <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
        <TitleHeading
          className="mb-[0.625rem]"
          title="조교관리"
          description="조교 관련 정보 화면입니다."
        />
        <Box className="flex justify-between">
          <h3 className="text-18 font-medium">급여관리</h3>
        </Box>
        <Box
          backgroundColor="gray"
          className="py-[0.625rem] px-[1.875rem] flex gap-[3.125rem] 1500:!pr-[6.5rem]"
        >
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">기간 검색</dt>
              <dd>
                <DateRangeField />
              </dd>
            </dl>
          </div>
          <div className="flex-1">
            <dl className="grid grid-cols-[7.5rem_1fr] 1600:grid-cols-[8.125rem_1fr] gap-y-[0.625rem]">
              <dt className="flex items-center">등급</dt>
              <dd>
                <Select name="grade" options={gradeOptions} />
              </dd>
              <dt className="flex items-center">검색 상세</dt>
              <dd className="flex gap-[0.313rem]">
                <EnhancedSearchWithTypeField
                  ref={searchRef}
                  options={userSearchTypeOptions}
                />
              </dd>
            </dl>
          </div>
        </Box>
        <div className="flex justify-end mt-[0.875rem] mb-[0.625rem]">
          <Button
            onClick={onExcelExport}
            className="w-[12.625rem]"
            color="green"
            disabled={!selection.length}
          >
            {`엑셀 다운로드 (${selection.length})`}
          </Button>
        </div>
        <Box className="!py-5 min-h-[calc(100%-20.188rem)] flex flex-col">
          <span className="text-16 font-medium text-secondary">
            총 {(totalItems ?? 0).toLocaleString()}개
          </span>
          <Table
            classNames={[
              "w-[4.8%] justify-center",
              "w-[6.67%]",
              "w-[9.3%]",
              "w-[9.3%] min-w-[7rem]",
              "w-[15.3%]",
              "w-[8%] min-w-[5.75rem]",
              "w-[8%] min-w-[5.75rem]",
              "w-[8%] min-w-[5.75rem]",
              "w-[14.6%]",
              "w-[16%]",
            ]}
          >
            <thead>
              <tr>
                <th>
                  <RawCheckbox
                    checked={
                      !!selection?.length && selection.length === data?.length
                    }
                    onChange={onSelectAll}
                  />
                </th>
                <th>번호</th>
                <th>등급</th>
                <th>이름(아이디)</th>
                <th>휴대전화번호</th>
                <th>
                  <SortButton name="answers" defaultValue="answers">
                    답변 수
                  </SortButton>
                </th>
                <th>
                  <SortButton name="warnings" defaultValue="answers">
                    경고 수
                  </SortButton>
                </th>
                <th>
                  <SortButton name="salary" defaultValue="answers">
                    총 급여
                  </SortButton>
                </th>
                <th>주민등록번호</th>
                <th>은행/계좌번호</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((user, index) => (
                <tr key={user.id}>
                  <td>
                    <RawCheckbox
                      checked={selection.includes(user.id)}
                      onChange={() => {
                        onSelectItem(user.id);
                      }}
                    />
                  </td>
                  <td>{totalItems - index - size * (currentPage - 1)}</td>
                  <td>{user.grade.name}</td>
                  <td>
                    {user.name}
                    {user ? `(${user.userId})` : ""}
                  </td>
                  <td>{addDashes(user.phone)}</td>
                  <td>{user.answerCount?.toLocaleString()}</td>
                  <td>{user.reportCount?.toLocaleString()}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => onIncomePreview(user.id)}
                      className="underline"
                    >
                      {(+user.reward!).toLocaleString()}
                    </button>
                  </td>
                  <td>
                    {user.residentId1}-{user.residentId2.slice(0, 1)}******
                  </td>
                  <td>
                    {user.bank}/{user.bankNumber}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Empty show={!isFetching && data && !data.length} />
          {/* <Pagination total={totalItems} pageSize={size} /> */}
        </Box>
      </main>
    </FormProvider>
  );
};

export default SettingsPaymentPage;
